import React from 'react';
import ReactDOM from 'react-dom';
import { getExerciseAnimationUrl } from '../../utils/exerciseImages';
import { isCardio, secondsToMMSS } from '../../utils/cardio';
import { MuscleMap } from './MuscleMap';

// =============================================
// WorkoutDetailsModal — the single, canonical finished-workout report.
// Same layout the admin Workout Instances explorer uses (stat tiles + session
// rating + numbered exercise cards with best-set/1RM). Bilingual so the SAME
// design renders in Spanish for clients and English for trainers/admins.
// =============================================

const LABELS = {
  en: {
    title: 'Workout Details', status: 'Status', finished: '✅ Finished',
    inProgress: '🔄 In Progress', notStarted: 'Not Started', exercises: 'Exercises',
    totalVolume: 'Total Volume', duration: 'Duration', effective: 'Effective',
    sessionRating: 'Session Rating', superset: 'Superset', bestSet: 'Best Set',
    est1rm: 'Est. 1RM', noSets: 'No sets recorded',
    noExercises: 'No exercises recorded in this workout', sessionNotes: 'Session Notes',
    close: 'Close', min: 'min', rest: 'Rest', muscles: 'Muscles worked',
  },
  es: {
    title: 'Detalles del Entrenamiento', status: 'Estado', finished: '✅ Finalizado',
    inProgress: '🔄 En curso', notStarted: 'No iniciado', exercises: 'Ejercicios',
    totalVolume: 'Volumen total', duration: 'Duración', effective: 'Efectivo',
    sessionRating: 'Valoración de la sesión', superset: 'Superserie', bestSet: 'Mejor serie',
    est1rm: '1RM est.', noSets: 'Sin series registradas',
    noExercises: 'No hay ejercicios registrados en este entrenamiento', sessionNotes: 'Notas de la sesión',
    close: 'Cerrar', min: 'min', rest: 'Descanso', muscles: 'Músculos trabajados',
  },
};

const calc1RM = (weight, reps) => (!weight || !reps || reps <= 0 ? 0 : weight * (1 + reps / 30));

// Rest taken after a set, shown as a subtle connector between set chips.
const fmtRestConnector = (sec) => {
  const n = parseInt(sec, 10);
  if (!n || isNaN(n) || n <= 0) return null;
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60);
  const s = n % 60;
  return s === 0 ? `${m}:00` : `${m}:${String(s).padStart(2, '0')}`;
};

const getBestSet = (exercise) => {
  const sets = exercise.actual_sets || [];
  const anyWeighted = sets.some((s) => (s.weight || 0) > 0);
  let best = null, bestScore = 0;
  sets.forEach((set, index) => {
    const w = set.weight || 0, r = set.reps || 0;
    // Bodyweight exercises rank by reps (0 kg is a valid PR); loaded lifts by 1RM.
    const score = anyWeighted ? calc1RM(w, r) : r;
    if (score > bestScore) {
      bestScore = score;
      best = { ...set, index, estimated1RM: anyWeighted ? calc1RM(w, r) : 0, bodyweight: !anyWeighted };
    }
  });
  return best;
};

const WorkoutDetailsModal = ({ workout, clientName, date, onClose, language = 'en' }) => {
  if (!workout) return null;
  const L = LABELS[language] || LABELS.en;

  const exercises = workout.exercises || [];
  const status = workout.is_finished || workout.status === 'finished'
    ? L.finished
    : workout.status === 'in_progress' ? L.inProgress : L.notStarted;
  const statusColor = workout.is_finished || workout.status === 'finished'
    ? 'text-green-400'
    : workout.status === 'in_progress' ? 'text-yellow-400' : 'text-gray-400';

  const totalVolume = exercises.reduce((sum, ex) =>
    sum + (ex.actual_sets || []).reduce((s, set) => s + ((set.weight || 0) * (set.reps || 0)), 0), 0);
  const volumeLabel = totalVolume > 1000 ? `${(totalVolume / 1000).toFixed(1)}k kg` : `${totalVolume} kg`;

  const effectiveLabel = (() => {
    if (workout.effective_time != null) return `${Math.round(workout.effective_time)} ${L.min}`;
    const total = workout.total_time;
    if (!total) return '-';
    let restMin = workout.rest_time;
    if (restMin == null) {
      let restSec = 0;
      exercises.forEach((ex) => (ex.actual_sets || []).forEach((s) => { restSec += (s.rest_time || 0); }));
      restMin = restSec / 60;
    }
    return `${Math.max(0, Math.round(total - restMin))} ${L.min}`;
  })();

  const hasLoggedSets = exercises.some((ex) => (ex.actual_sets || []).length > 0);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/70 flex items-start sm:items-center justify-center z-[9999] p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="workout-details-modal"
    >
      <div
        className="bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] my-auto overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-700 flex justify-between items-center flex-shrink-0 bg-gradient-to-r from-blue-900/50 to-purple-900/50">
          <div>
            <h3 className="text-lg font-bold text-white">📊 {L.title}</h3>
            <p className="text-sm text-gray-400">
              {clientName || workout.client_name || ''}{(clientName || workout.client_name) && (date || workout.date) ? ' • ' : ''}{date || workout.date || ''}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-2 text-xl font-bold" data-testid="workout-details-close">✕</button>
        </div>

        {/* Scrollable Content */}
        <div className="p-4 overflow-y-auto flex-1">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <div className="bg-gray-700/50 p-3 rounded-lg text-center">
              <div className="text-gray-400 text-xs">{L.status}</div>
              <div className={`font-medium ${statusColor}`}>{status}</div>
            </div>
            <div className="bg-gray-700/50 p-3 rounded-lg text-center">
              <div className="text-gray-400 text-xs">{L.exercises}</div>
              <div className="text-white font-bold text-xl">{exercises.length}</div>
            </div>
            <div className="bg-purple-900/30 p-3 rounded-lg text-center">
              <div className="text-purple-300 text-xs">{L.totalVolume}</div>
              <div className="text-purple-200 font-bold text-xl">{volumeLabel}</div>
            </div>
            <div className="bg-gray-700/50 p-3 rounded-lg text-center">
              <div className="text-gray-400 text-xs">{L.duration}</div>
              <div className="text-white font-bold text-xl">
                {workout.total_time ? `${Math.round(workout.total_time)} ${L.min}` : '-'}
              </div>
            </div>
            <div className="bg-emerald-900/30 p-3 rounded-lg text-center">
              <div className="text-emerald-300 text-xs">{L.effective}</div>
              <div className="text-emerald-200 font-bold text-xl">{effectiveLabel}</div>
            </div>
          </div>

          {/* Session Rating */}
          {workout.session_rating && (
            <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3 mb-4 text-center">
              <span className="text-yellow-300 text-sm">{L.sessionRating}: </span>
              <span className="text-yellow-200 font-bold text-lg">{workout.session_rating}/10 ⭐</span>
            </div>
          )}

          {/* Client's written feedback */}
          {workout.session_notes && workout.session_notes.trim() && (
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 mb-4" data-testid="workout-details-notes">
              <p className="text-blue-300 text-sm mb-1">📝 {L.sessionNotes}</p>
              <p className="text-white text-sm whitespace-pre-wrap break-words">{workout.session_notes}</p>
            </div>
          )}

          {/* Muscle map */}
          {hasLoggedSets && (
            <div className="mb-4 bg-gray-700/30 rounded-lg p-3 border border-gray-600/30" data-testid="workout-details-muscles">
              <h4 className="text-red-300 font-medium mb-3 text-sm flex items-center gap-2">💪 {L.muscles}</h4>
              <MuscleMap exercises={exercises} clientId={workout.client_id} language={language} shareable shareTitle={workout.template_name || L.muscles} shareDate={date || workout.date || ''} />
            </div>
          )}

          {/* Exercise List */}
          <div className="mb-4">
            <h4 className="text-blue-200 font-medium mb-3 text-sm flex items-center gap-2">
              🏋️ {L.exercises} ({exercises.length})
            </h4>
            <div className="space-y-3">
              {exercises.map((exercise, exIdx) => {
                const cardio = isCardio(exercise);
                const bestSet = cardio ? null : getBestSet(exercise);
                const actualSets = exercise.actual_sets || [];
                const exerciseVolume = actualSets.reduce((sum, set) => sum + ((set.weight || 0) * (set.reps || 0)), 0);
                const animUrl = getExerciseAnimationUrl(exercise);
                return (
                  <div key={exIdx} className="bg-gray-700/30 rounded-lg p-3 border border-gray-600/30">
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-20 h-20 bg-black/30 rounded-lg overflow-hidden">
                        {animUrl ? (
                          <img
                            src={animUrl}
                            alt={exercise.exercise_name_es || exercise.exercise_name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                          />
                        ) : null}
                        <div className={`w-full h-full items-center justify-center text-3xl ${animUrl ? 'hidden' : 'flex'}`} style={{ display: animUrl ? 'none' : 'flex' }}>🏋️</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded mr-2">{exIdx + 1}</span>
                            <span className="text-white font-semibold">
                              {language === 'es'
                                ? (exercise.exercise_name_es || exercise.exercise_name || 'Ejercicio')
                                : (exercise.exercise_name || exercise.exercise_name_es || 'Exercise')}
                            </span>
                            {exercise.is_superset && (
                              <span className="ml-2 bg-orange-500/30 text-orange-300 text-xs px-2 py-0.5 rounded">
                                🔗 {L.superset} {exercise.superset_group || 'A'}
                              </span>
                            )}
                            {cardio && (
                              <span className="ml-2 bg-cyan-500/20 text-cyan-300 text-xs px-2 py-0.5 rounded border border-cyan-500/40">
                                Cardio
                              </span>
                            )}
                          </div>
                          {exerciseVolume > 0 && (
                            <span className="text-blue-300 text-xs whitespace-nowrap">{exerciseVolume.toLocaleString()} kg</span>
                          )}
                        </div>
                        {/* Sets */}
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          {actualSets.length > 0 ? actualSets.map((set, setIdx) => {
                            const isBest = bestSet && setIdx === bestSet.index;
                            const restLabel = setIdx < actualSets.length - 1 ? fmtRestConnector(set.rest_time) : null;
                            return (
                              <React.Fragment key={setIdx}>
                                <div className={`px-2 py-1 rounded text-sm font-mono ${cardio ? 'bg-cyan-600/30 text-cyan-100' : isBest ? 'bg-yellow-500/30 border border-yellow-500 text-yellow-200' : 'bg-gray-600/50 text-gray-200'}`}>
                                  {cardio
                                    ? `⏱ ${secondsToMMSS(set.time) || '0:00'}`
                                    : <>{(set.weight || 0) > 0 ? `${set.reps || 0}×${set.weight}kg` : `${set.reps || 0} reps`}{isBest && <span className="ml-1">⭐</span>}</>}
                                </div>
                                {restLabel && (
                                  <span
                                    className="inline-flex items-center gap-0.5 text-[10px] text-gray-500 tabular-nums select-none"
                                    title={`${L.rest}: ${restLabel}`}
                                    data-testid={`set-rest-connector-${exIdx}-${setIdx}`}
                                  >
                                    <span className="text-gray-600">·</span>
                                    <span aria-hidden>⏱</span>
                                    {restLabel}
                                    <span className="text-gray-600">·</span>
                                  </span>
                                )}
                              </React.Fragment>
                            );
                          }) : (
                            <span className="text-gray-500 text-sm italic">{L.noSets}</span>
                          )}
                        </div>
                        {bestSet && (
                          <div className="mt-2 text-yellow-400 text-xs">
                            ⭐ {L.bestSet}: {bestSet.bodyweight ? `${bestSet.reps} reps` : `${bestSet.reps}×${bestSet.weight}kg`}
                            {!bestSet.bodyweight && (
                              <span className="text-yellow-500/70 ml-1">({L.est1rm}: {bestSet.estimated1RM.toFixed(1)}kg)</span>
                            )}
                          </div>
                        )}
                        {exercise.note && (
                          <p className="mt-2 text-yellow-300/80 text-xs italic">📝 {exercise.note}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {exercises.length === 0 && (
                <div className="text-center text-gray-500 py-8">{L.noExercises}</div>
              )}
            </div>
          </div>

          {/* Session Notes */}
          {workout.notes && (
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 mb-3">
              <p className="text-blue-300 text-xs font-medium mb-1">{L.sessionNotes}</p>
              <p className="text-white text-sm">{workout.notes}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex-shrink-0 flex justify-center">
          <button onClick={onClose} className="btn-secondary" data-testid="workout-details-close-btn">{L.close}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WorkoutDetailsModal;
